import { createSelector } from 'reselect'
import { GenericWorkout } from './../../types/generic-workout'
import { WorkoutResponse } from '../actions'
import { RootState } from '../reducers/rootReducers'
import { getTrainerName } from './workoutSelector'

export interface WorkoutObj {
  type: string
  workouts: GenericWorkout[]
}

const transformToGeneric = (w: WorkoutResponse): GenericWorkout => ({
  id: w.id,
  title: w.title,
  description: w.description,
  trainerName: w.trainer
    ? getTrainerName(w.trainer.firstName, w.trainer.lastName)
    : '',
  length: w.length,
  thumbnail: w.thumbnail,
  type: w.type,
  tags: w.tags,
  published: w.published,
  intensity: w.intensity,
  video: w.video,
  trainer: w.trainer,
  spotifyLink: w.spotifyLink,
  descriptionTwo: w.descriptionTwo,
})

const generateWorkoutObject = (workouts: GenericWorkout[]): WorkoutObj[] => {
  const workoutTypesJson = process.env.GATSBY_JSON
  const workoutTypes = JSON.parse(workoutTypesJson)

  const workoutObj: WorkoutObj[] = []

  workoutObj.push({
    type: workoutTypes.allWorkouts,
    workouts,
  })

  Object.keys(workoutTypes).forEach((key: string) => {
    if (key !== 'allWorkouts') {
      const filtered = workouts.filter(w => {
        const tags = w.tags || []
        return tags.includes(workoutTypes[key])
      })
      workoutObj.push({
        type: key,
        workouts: filtered,
      })
    }
  })

  return workoutObj
}

const workoutsState = (state: RootState) =>
  state.entities.workoutsReducer.workouts

export const workoutsSelector = (includeUnpublished: boolean) =>
  createSelector(
    workoutsState,
    (workouts: WorkoutResponse[]): WorkoutResponse[] => {
      if (workouts.length && typeof workouts === 'object') {
        return workouts.filter(w => {
          if (!includeUnpublished && !w.published) {
            return false
          }
          return true
        })
      }
      return []
    },
  )

export const workoutsPublishedSelector = createSelector(
  workoutsSelector(false),
  (workouts: WorkoutResponse[]): WorkoutObj[] => {
    if (workouts.length && typeof workouts === 'object') {
      const genericWorkouts = workouts.map((w: WorkoutResponse) =>
        transformToGeneric(w),
      )

      const workoutObj = generateWorkoutObject(genericWorkouts)

      return workoutObj
    }

    return []
  },
)

export const workoutsAllSelector = createSelector(
  workoutsSelector(true),
  (workouts: WorkoutResponse[]): WorkoutObj[] => {
    if (workouts.length && typeof workouts === 'object') {
      const genericWorkouts = workouts.map((w: WorkoutResponse) =>
        transformToGeneric(w),
      )

      const workoutObj = generateWorkoutObject(genericWorkouts)

      return workoutObj
    }

    return []
  },
)
